import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Form, Input, Modal, Select, message,
} from 'antd';

import { updateCompanyWhatsAppSender } from '../api';
import {
  AbtBotOption, SENDER_STATUSES, Sender, apiError,
} from './shared';

/** Select sentinels: the ABT endpoint has no start_module, so the current flow can't be preselected by id. */
const KEEP = '__keep__';
const NONE = '__none__';

interface Props {
  open: boolean;
  companyId: string;
  sender: Sender;
  abtBots: AbtBotOption[];
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Edit the company's WhatsApp number (ADR-037): display name, Apply-By-Text
 * flow, WABA id and status. Only changed fields are sent.
 */
const SenderEditModal: React.FC<Props> = ({
  open, companyId, sender, abtBots, onClose, onSaved,
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initialBot = sender.startModuleId ? KEEP : NONE;
  const initialStatus = String(sender.status || 'ACTIVE').toUpperCase();

  useEffect(() => {
    if (!open) return;
    setError(null);
    form.setFieldsValue({
      displayName: sender.displayName || '',
      abtBotId: initialBot,
      wabaId: sender.wabaId || '',
      status: initialStatus,
    });
  }, [open, sender, form, initialBot, initialStatus]);

  const botOptions = [
    ...(sender.startModuleId ? [{ value: KEEP, label: 'Keep current flow' }] : []),
    { value: NONE, label: 'No bot (inbox only)' },
    ...abtBots,
  ];

  const handleSave = async () => {
    let values: any;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const patch: Record<string, any> = {};
    const displayName = String(values.displayName || '').trim();
    if (displayName && displayName !== sender.displayName) patch.displayName = displayName;
    if (values.abtBotId !== initialBot) patch.abtBotId = values.abtBotId === NONE ? null : values.abtBotId;
    const wabaId = String(values.wabaId || '').trim();
    if (wabaId !== (sender.wabaId || '')) patch.wabaId = wabaId || null;
    if (values.status && values.status !== initialStatus) patch.status = values.status;

    if (!Object.keys(patch).length) {
      message.info('No changes to save');
      onClose();
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await updateCompanyWhatsAppSender(companyId, sender.botmapId, patch);
      message.success('WhatsApp number updated');
      onSaved();
    } catch (err) {
      setError(apiError(err, 'Failed to update the WhatsApp number.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Edit WhatsApp number"
      onCancel={onClose}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button key="save" type="primary" loading={saving} onClick={handleSave} data-testid="whatsapp-edit-save">
          Save
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" requiredMark={false} data-testid="whatsapp-edit-modal">
        <Form.Item
          name="displayName"
          label="Display name"
          rules={[{ required: true, whitespace: true, message: 'Enter a display name' }]}
        >
          <Input maxLength={120} autoComplete="off" />
        </Form.Item>
        <Form.Item
          name="abtBotId"
          label="Apply-By-Text bot"
          extra="The flow that answers candidates who message this number."
        >
          <Select showSearch optionFilterProp="label" options={botOptions} />
        </Form.Item>
        <Form.Item name="wabaId" label="WhatsApp Business Account ID" extra="Optional, from Meta Business Manager">
          <Input maxLength={64} autoComplete="off" />
        </Form.Item>
        <Form.Item
          name="status"
          label="Status"
          extra="Mirrors the number's state in Infobip for reference. Changing it here does not change Infobip."
        >
          <Select options={SENDER_STATUSES} />
        </Form.Item>
      </Form>
      {error && <Alert type="error" showIcon message={error} />}
    </Modal>
  );
};

export default SenderEditModal;
